import { useState } from "react";
import CopyCode from "../../CopyCode/CopyCode";
import Icon from "../../Icon";

const ToastStack = () => {
  const [toasts, setToasts] = useState([]);
  const [activeToast, setActiveToast] = useState("success");

  const addToast = (type) => {
    const id = Date.now();
    const newToast = { id, type, message: getToastMessage(type) };
    setToasts([...toasts, newToast]);
    setTimeout(() => removeToast(id), 3000);
  };

  const removeToast = (id) => {
    setToasts(toasts.filter(t => t.id !== id));
  };

  const getToastMessage = (type) => {
    const messages = {
      success: "Operation completed successfully!",
      error: "An error occurred. Please try again.",
      warning: "Warning: Please review this action.",
      info: "Information: Check the details below.",
      loading: "Processing your request...",
      default: "New notification received."
    };
    return messages[type] || messages.default;
  };

  const getToastStyle = (type) => {
    const styles = {
      success: "bg-green-900/30 border-green-700/50 text-green-300",
      error: "bg-red-900/30 border-red-700/50 text-red-300",
      warning: "bg-yellow-900/30 border-yellow-700/50 text-yellow-300",
      info: "bg-blue-900/30 border-blue-700/50 text-blue-300",
      loading: "bg-amber-900/30 border-amber-700/50 text-amber-300",
      default: "bg-slate-700/30 border-slate-600/50 text-slate-300"
    };
    return styles[type] || styles.default;
  };

  const getIconColor = (type) => {
    const colors = {
      success: "text-green-400",
      error: "text-red-400",
      warning: "text-yellow-400",
      info: "text-blue-400",
      loading: "text-amber-400",
      default: "text-slate-400"
    };
    return colors[type] || colors.default;
  };

  const getIconName = (type) => {
    const icons = {
      success: "check",
      error: "close",
      warning: "warning",
      info: "info",
      loading: "timer",
      default: "mail"
    };
    return icons[type] || icons.default;
  };

  return (
    <div className="flex flex-col gap-y-10 p-6">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-white mb-6">Toast Types</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Success Toast</label>
            <button
              onClick={() => addToast("success")}
              className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-green-100 text-sm rounded-lg mb-3 transition"
            >
              Show Success
            </button>
            <div className="h-20 bg-slate-800 rounded border border-slate-700 p-2">
              <div className="bg-green-900/30 border border-green-700/50 text-green-300 p-2 rounded text-xs flex items-center gap-2">
                <span><Icon name="check" className="w-4 h-4" /></span>
                <span>Operation completed successfully!</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="bg-green-900/30 border border-green-700/50 text-green-300 p-3 rounded">\n  <div className="flex items-center gap-2">\n    <span className="text-lg">Icon</span>\n    <span>Operation completed successfully!</span>\n  </div>\n</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Error Toast</label>
            <button
              onClick={() => addToast("error")}
              className="w-full px-3 py-2 bg-red-600 hover:bg-red-700 text-red-100 text-sm rounded-lg mb-3 transition"
            >
              Show Error
            </button>
            <div className="h-20 bg-slate-800 rounded border border-slate-700 p-2">
              <div className="bg-red-900/30 border border-red-700/50 text-red-300 p-2 rounded text-xs flex items-center gap-2">
                <span><Icon name="close" className="w-4 h-4" /></span>
                <span>An error occurred. Please try again.</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="bg-red-900/30 border border-red-700/50 text-red-300 p-3 rounded">\n  <div className="flex items-center gap-2">\n    <span className="text-lg">Icon</span>\n    <span>An error occurred. Please try again.</span>\n  </div>\n</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Warning Toast</label>
            <button
              onClick={() => addToast("warning")}
              className="w-full px-3 py-2 bg-yellow-600 hover:bg-yellow-700 text-yellow-100 text-sm rounded-lg mb-3 transition"
            >
              Show Warning
            </button>
            <div className="h-20 bg-slate-800 rounded border border-slate-700 p-2">
              <div className="bg-yellow-900/30 border border-yellow-700/50 text-yellow-300 p-2 rounded text-xs flex items-center gap-2">
                <span><Icon name="warning" className="w-4 h-4" /></span>
                <span>Warning: Please review this action.</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="bg-yellow-900/30 border border-yellow-700/50 text-yellow-300 p-3 rounded">\n  <div className="flex items-center gap-2">\n    <span className="text-lg">Icon</span>\n    <span>Warning: Please review this action.</span>\n  </div>\n</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Info Toast</label>
            <button
              onClick={() => addToast("info")}
              className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-blue-100 text-sm rounded-lg mb-3 transition"
            >
              Show Info
            </button>
            <div className="h-20 bg-slate-800 rounded border border-slate-700 p-2">
              <div className="bg-blue-900/30 border border-blue-700/50 text-blue-300 p-2 rounded text-xs flex items-center gap-2">
                <span><Icon name="info" className="w-4 h-4" /></span>
                <span>Information: Check the details below.</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="bg-blue-900/30 border border-blue-700/50 text-blue-300 p-3 rounded">\n  <div className="flex items-center gap-2">\n    <span className="text-lg">Icon</span>\n    <span>Information: Check the details below.</span>\n  </div>\n</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Loading Toast</label>
            <button
              onClick={() => addToast("loading")}
              className="w-full px-3 py-2 bg-amber-600 hover:bg-amber-700 text-amber-100 text-sm rounded-lg mb-3 transition"
            >
              Show Loading
            </button>
            <div className="h-20 bg-slate-800 rounded border border-slate-700 p-2">
              <div className="bg-amber-900/30 border border-amber-700/50 text-amber-300 p-2 rounded text-xs flex items-center gap-2">
                <span className="inline-block w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin"></span>
                <span>Processing your request...</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="bg-amber-900/30 border border-amber-700/50 text-amber-300 p-3 rounded">\n  <div className="flex items-center gap-2">\n    <div className="w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin"></div>\n    <span>Processing your request...</span>\n  </div>\n</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Closeable Toast</label>
            <button
              onClick={() => addToast("default")}
              className="w-full px-3 py-2 bg-slate-600 hover:bg-slate-700 text-slate-100 text-sm rounded-lg mb-3 transition"
            >
              Show Closeable
            </button>
            <div className="h-20 bg-slate-800 rounded border border-slate-700 p-2">
              <div className="bg-slate-700/30 border border-slate-600/50 text-slate-300 p-2 rounded text-xs flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span><Icon name="mail" className="w-4 h-4" /></span>
                  <span>New notification received.</span>
                </div>
                <button className="text-slate-400 hover:text-slate-200 font-bold"><Icon name="close" className="w-4 h-4" /></button>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="bg-slate-700/30 border border-slate-600/50 text-slate-300 p-3 rounded flex items-center justify-between">\n  <div className="flex items-center gap-2">\n    <span>Icon</span>\n    <span>New notification received.</span>\n  </div>\n  <button className="text-slate-400 hover:text-slate-200 font-bold">Close</button>\n</div>`} />
          </div>
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-amber-400 mb-6">Toast Positions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Top-Left Position</label>
            <div className="h-24 bg-slate-800 rounded border border-slate-700 relative overflow-hidden">
              <div className="absolute top-2 left-2 bg-green-900/30 border border-green-700/50 text-green-300 p-2 rounded text-xs flex items-center gap-2">
                <span><Icon name="check" className="w-4 h-4" /></span>
                <span>Toast</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="fixed top-4 left-4 z-50">\n  <div className="bg-green-900/30 border border-green-700/50 text-green-300 p-3 rounded">\n    Success Toast\n  </div>\n</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Top-Center Position</label>
            <div className="h-24 bg-slate-800 rounded border border-slate-700 relative overflow-hidden flex justify-center">
              <div className="absolute top-2 bg-blue-900/30 border border-blue-700/50 text-blue-300 p-2 rounded text-xs flex items-center gap-2">
                <span><Icon name="info" className="w-4 h-4" /></span>
                <span>Toast</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">\n  <div className="bg-blue-900/30 border border-blue-700/50 text-blue-300 p-3 rounded">\n    Info Toast\n  </div>\n</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Top-Right Position</label>
            <div className="h-24 bg-slate-800 rounded border border-slate-700 relative overflow-hidden justify-end flex">
              <div className="absolute top-2 right-2 bg-yellow-900/30 border border-yellow-700/50 text-yellow-300 p-2 rounded text-xs flex items-center gap-2">
                <span><Icon name="warning" className="w-4 h-4" /></span>
                <span>Toast</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="fixed top-4 right-4 z-50">\n  <div className="bg-yellow-900/30 border border-yellow-700/50 text-yellow-300 p-3 rounded">\n    Warning Toast\n  </div>\n</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Bottom-Left Position</label>
            <div className="h-24 bg-slate-800 rounded border border-slate-700 relative overflow-hidden flex flex-col justify-end">
              <div className="absolute bottom-2 left-2 bg-red-900/30 border border-red-700/50 text-red-300 p-2 rounded text-xs flex items-center gap-2">
                <span><Icon name="close" className="w-4 h-4" /></span>
                <span>Toast</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="fixed bottom-4 left-4 z-50">\n  <div className="bg-red-900/30 border border-red-700/50 text-red-300 p-3 rounded">\n    Error Toast\n  </div>\n</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Bottom-Center Position</label>
            <div className="h-24 bg-slate-800 rounded border border-slate-700 relative overflow-hidden flex flex-col justify-end items-center">
              <div className="absolute bottom-2 bg-amber-900/30 border border-amber-700/50 text-amber-300 p-2 rounded text-xs flex items-center gap-2">
                <span className="inline-block w-3 h-3 border-2 border-transparent border-t-current rounded-full animate-spin"></span>
                <span>Toast</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">\n  <div className="bg-amber-900/30 border border-amber-700/50 text-amber-300 p-3 rounded">\n    Loading Toast\n  </div>\n</div>`} />
          </div>

          <div className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
            <label className="text-xs text-slate-400 mb-3 block font-medium">Bottom-Right Position</label>
            <div className="h-24 bg-slate-800 rounded border border-slate-700 relative overflow-hidden flex flex-col justify-end items-end">
              <div className="absolute bottom-2 right-2 bg-slate-700/30 border border-slate-600/50 text-slate-300 p-2 rounded text-xs flex items-center gap-2">
                <span><Icon name="mail" className="w-4 h-4" /></span>
                <span>Toast</span>
              </div>
            </div>
            <CopyCode number_code={Math.floor(Math.random() * 10000)} code={`<div className="fixed bottom-4 right-4 z-50">\n  <div className="bg-slate-700/30 border border-slate-600/50 text-slate-300 p-3 rounded">\n    Default Toast\n  </div>\n</div>`} />
          </div>
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
        <h2 className="text-xl font-bold text-orange-400 mb-6 inline-flex items-center gap-2"><Icon name="bell" className="w-5 h-5" /> Stacked Toasts Preview</h2>
        <div className="relative h-40 bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
          <div className="absolute top-2 right-2 space-y-2 w-72">
            {toasts.slice(-3).map((toast, idx) => (
              <div 
                key={toast.id}
                className={`${getToastStyle(toast.type)} border p-3 rounded-lg text-xs flex items-center justify-between animate-in fade-in slide-in-from-top duration-300`}
                style={{animation: `slideIn 0.3s ease-out`}}
              >
                <span className="inline-flex items-center gap-2"><Icon name={getIconName(toast.type)} className={`w-4 h-4 ${getIconColor(toast.type)}`} />{toast.message}</span>
                <button 
                  onClick={() => removeToast(toast.id)}
                  className="ml-2 hover:opacity-70 font-bold"
                >
                  <Icon name="close" className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="p-3 text-xs text-slate-400">
            Click buttons above to see stacked toasts appear here...
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ToastStack;


